import { useAuth } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  HelpCircle, 
  Mail, 
  LayoutDashboard, 
  FileText, 
  Calendar, 
  Settings, 
  Users, 
  CheckCircle, 
  XCircle, 
  Plus,
  Eye,
  Shield,
  Bell,
  Palette,
  Clock,
  Flag,
  Building,
  CreditCard,
  Crown,
  Briefcase,
  Key,
  AlertTriangle
} from "lucide-react";

export default function InstructionsPage() {
  const { user } = useAuth();
  const ruolo = user?.ruolo;

  // Instructions for Dipendente
  const dipendenteInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "La tua pagina principale dove puoi:",
      items: [
        "Visualizzare il riepilogo delle tue ferie e permessi dell'anno (se abilitato dal Titolare)",
        "Vedere lo stato delle tue richieste (in attesa, approvate, rifiutate)",
        "Creare una nuova richiesta di ferie o permesso con il pulsante '+ Nuova Richiesta'",
        "Consultare lo storico delle tue richieste recenti"
      ]
    },
    {
      icon: FileText,
      title: "Richieste",
      description: "Gestisci tutte le tue richieste:",
      items: [
        "Visualizza lo storico completo delle tue richieste",
        "Filtra per stato: Tutte / In attesa / Approvate / Rifiutate",
        "Controlla i dettagli di ogni richiesta (date, ore, note)",
        "Crea nuove richieste di ferie o permessi ROL",
        "🗑️ Cancella le richieste in attesa se hai commesso errori (date sbagliate, note errate)"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Visualizza il calendario aziendale:",
      items: [
        "Vedi le ferie e permessi approvati di tutto il team",
        "Consulta le festività nazionali e locali",
        "Visualizza eventi speciali (straordinari, malattie, ecc.)"
      ]
    },
    {
      icon: Settings,
      title: "Impostazioni",
      description: "Personalizza le tue preferenze email:",
      items: [
        "Scegli quali notifiche email ricevere",
        "Attiva/disattiva notifiche per richieste approvate o rifiutate",
        "Imposta notifiche per nuovi eventi in calendario"
      ]
    }
  ];

  // Instructions for Amministrativo
  const amministrativoInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "La tua pagina principale dove puoi:",
      items: [
        "Visualizzare il tuo riepilogo personale di ferie e permessi (se abilitato dal Titolare)",
        "Creare nuove richieste di ferie o permessi",
        "Consultare lo storico delle tue richieste"
      ]
    },
    {
      icon: FileText,
      title: "Richieste",
      description: "Gestisci le tue richieste personali:",
      items: [
        "Visualizza lo storico delle tue richieste",
        "Filtra per stato: Tutte / In attesa / Approvate / Rifiutate",
        "Crea nuove richieste di ferie o permessi",
        "🗑️ Cancella le richieste in attesa se hai commesso errori"
      ]
    },
    {
      icon: Users,
      title: "Dipendenti",
      description: "Visualizza l'elenco del team:",
      items: [
        "Consulta l'elenco completo dei dipendenti",
        "Se autorizzato dal Titolare: configura ferie e permessi dei dipendenti",
        "Visualizza i dati di ogni utente (nome, ruolo, posizione)"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Gestisci il calendario aziendale:",
      items: [
        "Vedi le ferie e permessi approvati del team",
        "Se autorizzato: aggiungi eventi come Straordinario o Malattia",
        "Consulta festività nazionali e locali"
      ]
    },
    {
      icon: Settings,
      title: "Impostazioni",
      description: "Accedi alle impostazioni in base ai permessi assegnati dal Titolare:",
      items: [
        "Email: gestisci le tue notifiche personali",
        "Se autorizzato: Settimana lavorativa, Festività",
        "I permessi vengono assegnati dal Titolare nella sezione 'Gestione Dipendenti'"
      ]
    },
    {
      icon: Shield,
      title: "Permessi Speciali",
      description: "Il Titolare può assegnarti permessi per:",
      items: [
        "Configurare ferie e permessi dei dipendenti (icona calendario verde)",
        "Aggiungere eventi nel calendario (straordinari, malattie)"
      ]
    }
  ];

  // Instructions for Titolare
  const titolareInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "Il centro di controllo della tua azienda:",
      items: [
        "Clicca su 'Richieste in Attesa' per andare direttamente ad approvare/rifiutare",
        "Visualizza il numero totale di dipendenti",
        "Consulta il 'Prospetto Ferie Dipendenti' con la situazione completa di ogni dipendente",
        "Vedi per ogni dipendente: richieste in attesa, ferie approvate, ferie residue, permessi approvati, ore residue"
      ]
    },
    {
      icon: FileText,
      title: "Richieste",
      description: "Gestione completa delle richieste:",
      items: [
        "Visualizza tutte le richieste di tutti i dipendenti",
        "Filtra per stato: Tutte / In attesa / Approvate / Rifiutate",
        "Approva o rifiuta direttamente dalla lista",
        "Puoi anche creare richieste per te stesso"
      ]
    },
    {
      icon: Users,
      title: "Dipendenti",
      description: "Amministra il tuo team (accessibile dal menu laterale):",
      items: [
        "📅 Icona Calendario: configura ferie e permessi annuali del dipendente",
        "🛡️ Icona Scudo: assegna permessi speciali agli Amministrativi",
        "✏️ Icona Matita: modifica dati utente (nome, ruolo, posizione)",
        "🗑️ Icona Cestino: elimina utenti"
      ]
    },
    {
      icon: Shield,
      title: "Permessi Amministrativi",
      description: "Cliccando sullo scudo di un Amministrativo puoi autorizzarlo a:",
      items: [
        "Configurare ferie e permessi dei dipendenti",
        "Aggiungere eventi nel calendario (straordinari, malattie)"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Calendario aziendale completo:",
      items: [
        "Vedi le ferie e permessi approvati del team",
        "Aggiungi eventi speciali (Straordinario, Malattia, Formazione, ecc.)",
        "Visualizza festività nazionali e locali"
      ]
    },
    {
      icon: Settings,
      title: "Impostazioni",
      description: "Configurazione completa dell'applicazione:",
      items: [
        "Generale: Nome azienda, logo e Codice Invito per i nuovi dipendenti",
        "Aspetto: Scegli il colore tema dell'applicazione",
        "Settimana: Configura giorni lavorativi e chiusura infrasettimanale",
        "Limiti: Imposta fino a quando si possono richiedere ferie/permessi",
        "Festività: Aggiungi festività locali (es. Santo Patrono)",
        "Email: Configura le notifiche che vuoi ricevere",
        "Visibilità: Decidi se i dipendenti possono vedere le proprie ferie residue"
      ]
    },
    {
      icon: Eye,
      title: "Visibilità Ferie Residue",
      description: "Controlla cosa vedono i dipendenti:",
      items: [
        "Vai in Impostazioni > Visibilità",
        "Attiva/disattiva la visualizzazione delle ferie residue per i dipendenti",
        "Se disattivato, i dipendenti possono solo inviare richieste senza vedere i saldi"
      ]
    },
    {
      icon: CreditCard,
      title: "Abbonamento",
      description: "Gestisci il tuo abbonamento dalla pagina dedicata:",
      items: [
        "Visualizza il piano attuale e i limiti dipendenti",
        "Scegli tra i piani: Base, Standard, Premium, Enterprise",
        "Effettua l'upgrade con Carta di Credito (Stripe) o PayPal",
        "Richiedi pagamento tramite Bonifico Bancario",
        "Piano Personalised: per aziende multi-filiale, richiedi preventivo",
        "Periodo di prova: 14 giorni gratuiti per nuove aziende"
      ]
    },
    {
      icon: Key,
      title: "Codice Invito",
      description: "Gestisci l'accesso alla tua azienda:",
      items: [
        "Trova il codice invito nelle Impostazioni > Generale",
        "Condividi il codice con i nuovi dipendenti per la registrazione",
        "I dipendenti useranno questo codice durante la registrazione"
      ]
    }
  ];

  // Select instructions based on role
  const getInstructions = () => {
    switch (ruolo) {
      case "titolare":
        return titolareInstructions;
      case "amministrativo":
        return amministrativoInstructions;
      default:
        return dipendenteInstructions;
    }
  };

  const getRoleTitle = () => {
    switch (ruolo) {
      case "titolare":
        return "Titolare";
      case "amministrativo":
        return "Amministrativo";
      default:
        return "Dipendente";
    }
  };

  const getRoleIcon = () => {
    switch (ruolo) {
      case "titolare":
        return Crown;
      case "amministrativo":
        return Shield;
      default:
        return Briefcase;
    }
  };

  const instructions = getInstructions();
  const RoleIcon = getRoleIcon();

  // Subscription plans for Titolare
  const subscriptionPlans = [
    {
      name: "Base",
      price: "€29,90 + €2,90/mese",
      employees: "Fino a 5 dipendenti",
      features: []
    },
    {
      name: "Standard",
      price: "€39,00 + €4,90/mese",
      employees: "Fino a 15 dipendenti",
      features: [],
      highlighted: true
    },
    {
      name: "Premium",
      price: "€49,00 + €5,90/mese",
      employees: "Fino a 30 dipendenti",
      features: []
    },
    {
      name: "Enterprise",
      price: "€59,00 + €9,90/mese",
      employees: "Dipendenti illimitati",
      features: []
    },
    {
      name: "Personalised",
      price: "Su richiesta",
      employees: "Multi-filiale",
      features: []
    }
  ];

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center p-3 rounded-full mb-4" style={{ backgroundColor: 'var(--theme-100)' }}>
            <RoleIcon className="h-8 w-8" style={{ color: 'var(--theme-600)' }} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Guida per {getRoleTitle()}
          </h1>
          <p className="text-slate-500">
            Scopri come utilizzare al meglio l'applicazione
          </p>
        </div>

        {/* Instructions Cards */}
        <div className="space-y-6">
          {instructions.map((section, index) => {
            const Icon = section.icon;
            return (
              <Card key={index} className="border-slate-200 overflow-hidden">
                <CardHeader className="pb-3" style={{ backgroundColor: 'var(--theme-50)' }}>
                  <CardTitle className="flex items-center gap-3 text-lg">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--theme-100)' }}>
                      <Icon className="h-5 w-5" style={{ color: 'var(--theme-600)' }} />
                    </div>
                    {section.title}
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    {section.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 mt-0.5 shrink-0" style={{ color: 'var(--theme-500)' }} />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Subscription Plans - Only for Titolare */}
        {ruolo === "titolare" && (
          <>
            <Separator className="my-8" />
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center p-3 rounded-full mb-2" style={{ backgroundColor: 'var(--theme-100)' }}>
                  <CreditCard className="h-6 w-6" style={{ color: 'var(--theme-600)' }} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Piani di Abbonamento
                </h2>
                <p className="text-slate-500">
                  Scegli il piano più adatto alla tua azienda
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                {subscriptionPlans.map((plan, index) => (
                  <Card 
                    key={index} 
                    className={`border-2 transition-all hover:shadow-lg ${
                      plan.highlighted 
                        ? 'border-theme-500 shadow-md' 
                        : 'border-slate-200'
                    }`}
                  >
                    {plan.highlighted && (
                      <div className="text-center py-1 text-xs font-medium text-white" style={{ backgroundColor: 'var(--theme-500)' }}>
                        Più popolare
                      </div>
                    )}
                    <CardHeader className="text-center pb-2 px-3">
                      <CardTitle className="text-lg">{plan.name}</CardTitle>
                      <div className="mt-1">
                        <span className="text-xl font-bold text-slate-900">{plan.price}</span>
                      </div>
                      <CardDescription className="text-sm font-medium" style={{ color: 'var(--theme-600)' }}>
                        {plan.employees}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="px-3 pt-0">
                      <ul className="space-y-1.5">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center gap-1.5 text-xs text-slate-600">
                            <CheckCircle className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--theme-500)' }} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <p className="text-center text-sm text-slate-500">
                Tutti i piani includono 14 giorni di prova gratuita. Per il piano Personalised contatta support@ferie-e-permessi.it
              </p>
            </div>
          </>
        )}

        {/* Disclaimer Section */}
        <Separator className="my-8" />
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              Avviso Importante
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-amber-800 space-y-2">
            <p>I dati visualizzati sono puramente indicativi e potrebbero non rispecchiare la situazione reale.</p>
            <p>Non hanno alcun valore legale e non possono essere utilizzati per fini ufficiali.</p>
            <p>Per i dati certificati fare sempre riferimento alla busta paga o alla documentazione fornita dall'azienda.</p>
            <p>Da tali informazioni non deriva alcun diritto né obbligo per nessuna delle parti.</p>
          </CardContent>
        </Card>

        {/* Footer - Help & Credits */}
        <Separator className="my-8" />
        <div className="text-center space-y-6 pb-8">
          {/* Logo */}
          <div className="flex justify-center">
            <img 
              src="/logo_ferie_permessi.jpg" 
              alt="Ferie e Permessi" 
              className="h-32 w-auto object-contain rounded-lg shadow-md"
            />
          </div>
          
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center justify-center gap-2">
              <HelpCircle className="h-5 w-5" style={{ color: 'var(--theme-500)' }} />
              Hai bisogno di aiuto?
            </h3>
            <p className="text-slate-600">
              Contatta il nostro supporto per qualsiasi domanda o problema
            </p>
            <Button 
              className="btn-theme"
              onClick={() => window.location.href = 'mailto:support@ferie-e-permessi.it'}
            >
              <Mail className="h-4 w-4 mr-2" />
              support@ferie-e-permessi.it
            </Button>
          </div>

          <div className="pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-400">
              Creato da <span className="font-semibold text-slate-600">LuiCas</span> ™
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
